import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Daily Schedule v2 API client (api-daily-schedule-v2-service). Twelfth of
 * the v2 fleet — same shape as the other v2 clients.
 *
 * Contract notes:
 *
 * 1. The store is ONE DOCUMENT PER PATIENT PER CALENDAR DAY, mirroring the
 *    patient app's routine reporting. Every day-scoped call takes a `date`
 *    ("YYYY-MM-DD", the clinician's local day — see data.todayDate). GET
 *    returns `{filled, version, entries, hydration, sleep}`; an unfilled
 *    day is `filled:false` with no entries and version 0 — nothing is
 *    seeded or persisted by a read.
 *
 * 2. `days()` pages the filled days newest-first (cursor on the date
 *    string: pass the previous page's `nextBefore`). `template()` returns
 *    the default 14-row timeline the UI offers on an unfilled day.
 *
 * 3. Every mutating call carries `expectedVersion` (last version the caller
 *    saw for THAT day). On mismatch the server returns 409 with
 *    `{reason:"version", current:<day>}` — callers rebase on `current`.
 *
 * 4. Duplicate rule (server-side, per day): two entries with the same
 *    `timeMinutes` OR the same normalized activity name are rejected — the
 *    frontend guards these too so the user sees the error inline.
 *
 * 5. Delete is soft — server flips `deleted=true`; the row disappears from
 *    subsequent list responses but stays on-doc for the audit trail.
 *
 * 6. Hydration + Sleep are single-block replacements on the same day doc;
 *    each PUT appends the previous values to `history[]` server-side. Sleep
 *    only carries `quality` (sleep-time / wake-time / duration are derived
 *    on the FE from the "Sleep" and "Wake up" timeline entries).
 */
class DailyScheduleService {
  constructor() {
    this.apiService = new APIService();
  }

  template(onResponse, onError) {
    return this.apiService.get(APIConfig.dailyScheduleV2.template, onResponse, onError);
  }

  list(patientId, date, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.list.format(patientId, date);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Filled days, newest first. `opts.limit` (default 20, max 2000),
   * `opts.before` (exclusive date cursor), `opts.since` (inclusive lower
   * bound) and `opts.offset` (numbered pages) map straight to the query
   * string.
   */
  days(patientId, opts, onResponse, onError) {
    const q = [];
    if (opts && opts.limit) q.push(`limit=${encodeURIComponent(opts.limit)}`);
    if (opts && opts.before) q.push(`before=${encodeURIComponent(opts.before)}`);
    // Numbered pages — page N of `limit` per page is offset (N-1)*limit.
    if (opts && opts.offset) q.push(`offset=${encodeURIComponent(opts.offset)}`);
    // Inclusive lower bound — the "everything in the last five years" fetch.
    if (opts && opts.since) q.push(`since=${encodeURIComponent(opts.since)}`);
    const url =
      APIConfig.dailyScheduleV2.days.format(patientId) + (q.length ? `?${q.join("&")}` : "");
    return this.apiService.get(url, onResponse, onError);
  }

  addEntry(patientId, date, body, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.addEntry.format(patientId, date);
    return this.apiService.post(url, body, onResponse, onError);
  }

  updateEntry(patientId, date, entryId, body, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.updateEntry.format(patientId, entryId, date);
    return this.apiService.put(url, body, onResponse, onError);
  }

  /**
   * Bulk-replace the day's entries[] in one round-trip. Used by the "Save"
   * flow on an unfilled day (version === 0): the template or a hand-built
   * list is edited locally and committed here. Body shape matches
   * beans.BatchReplaceEntriesRequest:
   *   { entries: [{timeMinutes, activity}, ...], expectedVersion }
   */
  batchReplaceEntries(patientId, date, body, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.batchEntries.format(patientId, date);
    return this.apiService.post(url, body, onResponse, onError);
  }

  updateHydration(patientId, date, body, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.updateHydration.format(patientId, date);
    return this.apiService.put(url, body, onResponse, onError);
  }

  updateSleep(patientId, date, body, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.updateSleep.format(patientId, date);
    return this.apiService.put(url, body, onResponse, onError);
  }

  trendEntry(patientId, entryId, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.trendEntry.format(patientId, entryId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Soft-delete. Body carries `expectedVersion` for optimistic locking.
   * APIService.delete has no body parameter — direct fetch pattern
   * matches other v2 services in this codebase.
   */
  deleteEntry(patientId, date, entryId, expectedVersion, onResponse, onError) {
    const url = APIConfig.dailyScheduleV2.deleteEntry.format(patientId, entryId, date);
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default DailyScheduleService;
